import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  PSP,
  deleteAllCreditCards,
  deleteCreditCard,
  selectCreditCard,
  BaseCreditCardRequestParamsPM,
} from '@services/creditCardService';
import { CreditCardData } from '@types-internal';
import { deleteCardArrayAndUpdate, selectCardAndUnselectPrevious } from '@utils/cardUtils';

import { useAppContext } from './AppContextProvider';
import { OverlayIds, useOverlays } from './OverlaysProvider';

export interface CreditCardsMutationData {
  btToken?: string;
  cardToDelete?: CreditCardData;
  creditCards: CreditCardData[];
}

interface ContextValues {
  initiateCardDeletion: (creditCard: CreditCardData) => void;
  deleteCard: () => Promise<void>;
  deleteAllCards: () => Promise<boolean>;
  makeCreditCardActive: (creditCard: CreditCardData) => Promise<boolean>;
  data: CreditCardsMutationData;
}

const CreditCardsMutationContext = createContext<ContextValues>({
  initiateCardDeletion: () => null,
  deleteCard: () => Promise.resolve(),
  deleteAllCards: () => Promise.resolve(false),
  makeCreditCardActive: () => Promise.resolve(false),
  data: { creditCards: [] },
});

interface CreditCardsMutationProps {
  pspToken: string;
  psp: PSP;
  creditCards: CreditCardData[];
  paymentMethod: BaseCreditCardRequestParamsPM;
}

/**
 * Manages the list of credit cards of the customer, by proposing to the other components the methods to select, delete and add credit cards.
 * Each method will call the appropriate method from the creditCardService to send a request to the backend.
 */
const CreditCardsMutationProvider: React.FC<PropsWithChildren<CreditCardsMutationProps>> = ({
  children,
  pspToken,
  psp,
  creditCards: inputCards,
  paymentMethod,
}): ReactElement => {
  const { csrfToken } = useAppContext();
  const { showOverlay } = useOverlays();
  const [creditCards, setCreditCards] = useState<CreditCardData[]>(inputCards);
  const [cardToDelete, setCartToDelete] = useState<CreditCardData>();

  const initiateCardDeletion = (card: CreditCardData) => {
    setCartToDelete(card);
    showOverlay(OverlayIds.DELETE_CARD);
  };

  const makeCreditCardActive = async (creditCard: CreditCardData) => {
    // Optimistic UI: we display the card as selected before actually making the request
    const previousSelectedCard = creditCards.find(c => c.selected);
    setCreditCards(currentCreditCards =>
      selectCardAndUnselectPrevious(currentCreditCards, creditCard.pspIdentifier),
    );

    function revertOptimisticUI() {
      setCreditCards(currentCreditCards =>
        selectCardAndUnselectPrevious(
          currentCreditCards,
          previousSelectedCard ? previousSelectedCard.pspIdentifier : null,
        ),
      );
    }

    try {
      const isSuccess = await selectCreditCard({
        csrfToken: csrfToken as string, // todo: how to be sure we do have a string and never undefined?
        creditCardIdentifier: creditCard.pspIdentifier,
        psp,
        paymentMethod,
      });

      if (!isSuccess) {
        revertOptimisticUI();
      }
      return isSuccess;
    } catch (error) {
      // just catch the error in passing, to ensure to revert the UI since the card could not be selected
      revertOptimisticUI();
      throw error;
    }
  };

  const deleteCard = async () => {
    if (!cardToDelete) {
      return;
    }

    const newSelectedCardPspIdentifier = await deleteCreditCard({
      csrfToken: csrfToken as string,
      creditCardIdentifier: cardToDelete.pspIdentifier,
      paymentMethod,
      psp,
    });

    // Remove deleted and update selection if changed
    const updatedCards = deleteCardArrayAndUpdate(
      creditCards,
      cardToDelete,
      newSelectedCardPspIdentifier,
    );
    setCreditCards(updatedCards);
  };

  const deleteAllCards = async () => {
    const isSuccess = await deleteAllCreditCards({
      csrfToken: csrfToken as string,
      psp,
      paymentMethod,
    });
    setCreditCards([]);
    return isSuccess;
  };

  useEffect(() => {
    setCreditCards(inputCards);
  }, [inputCards]);

  return (
    <CreditCardsMutationContext.Provider
      value={{
        initiateCardDeletion,
        deleteCard,
        deleteAllCards,
        makeCreditCardActive,
        data: {
          btToken: pspToken,
          cardToDelete,
          creditCards,
        },
      }}>
      {children}
    </CreditCardsMutationContext.Provider>
  );
};

export const useCreditcardService = () => useContext(CreditCardsMutationContext);

export default CreditCardsMutationProvider;
